import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { fetchApiAuth } from '../utils/api';
import {
    FtcSession,
    FtcSessionEntry,
    FtcSessionEntryResponse,
    FtcSessionResponse,
    FtcMessageResponse,
    ProjectGuarding,
    ProjectGuardingListResponse,
    ProjectGuardingResponse,
    CreateProjectGuardingPayload,
    FtcAnswer,
} from '../sites/types';
import { handleError } from '../utils/helpers';

export interface FindStoreResult {
    siteId: number;
    storeCode: string;
    responseId: string;
    siteName: string | null;
    siteAddress: string | null;
    sitePostcode: string | null;
    suggestedStatus: 'ftc' | 'guarding' | 'no_access';
}

// A post code covering several stores returns them all for the user to pick.
export interface FindStoreMultipleResult {
    multipleSites: true;
    message: string;
    sites: FindStoreResult[];
}

export type FindStoreLookup = FindStoreResult | FindStoreMultipleResult;

export const isMultipleSites = (result: FindStoreLookup): result is FindStoreMultipleResult => (
    'multipleSites' in result && result.multipleSites === true
);

interface FindStoreResponse {
    data: FindStoreLookup;
}

export interface SaveAndSendEntry {
    siteId: number;
    storeCode: string;
    responseId: string;
    status: 'ftc' | 'guarding' | 'no_access';
}

type FtcMessagePayload = FtcMessageResponse['data'];

const fallbackMessage = (errorMessage: string | false, fallback: string): string => (
    errorMessage === false ? fallback : errorMessage
);

export const fetchCurrentSession = createAsyncThunk<
    FtcSession,
    void,
    { rejectValue: string }
>(
    'ftc/fetchCurrentSession',
    async (_, { rejectWithValue }) => {
        try {
            const response: FtcSessionResponse = await fetchApiAuth({
                method: 'GET',
                url: '/ftc/current',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to load FTC session');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const findStore = createAsyncThunk<
    FindStoreLookup,
    string,
    { rejectValue: string }
>(
    'ftc/findStore',
    async (storeCode, { rejectWithValue }) => {
        try {
            const response: FindStoreResponse = await fetchApiAuth({
                method: 'POST',
                url: '/ftc/find-store',
                body: { storeCode },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to find store');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const saveAndSend = createAsyncThunk<
    FtcMessagePayload,
    SaveAndSendEntry[],
    { rejectValue: string }
>(
    'ftc/saveAndSend',
    async (entries, { rejectWithValue }) => {
        try {
            const response: FtcMessageResponse = await fetchApiAuth({
                method: 'POST',
                url: '/ftc/save-and-send',
                body: { entries },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to save FTC entries');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const fetchUpdateEntries = createAsyncThunk<
    FtcSession,
    void,
    { rejectValue: string }
>(
    'ftc/fetchUpdateEntries',
    async (_, { rejectWithValue }) => {
        try {
            const response: FtcSessionResponse = await fetchApiAuth({
                method: 'GET',
                url: '/ftc/update',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to load FTC update entries');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

// Every field is nullable: an entry is stored as information arrives and only
// has to be complete before the update report can be sent.
export interface UpdateEntryPayload {
    entryId: number;
    timeOn: string | null;
    timeOff: string | null;
    internalPatrol: FtcAnswer | null;
    externalPatrol: FtcAnswer | null;
    idsFobHeld: FtcAnswer | null;
    idsFobWorks: FtcAnswer | null;
    storeKeysHeld: FtcAnswer | null;
    storeKeysWork: FtcAnswer | null;
    shutterKeyHeld: FtcAnswer | null;
    shutterKeyWorks: FtcAnswer | null;
    responseSetIdsOnDeparture: FtcAnswer | null;
    remoteEngSetOnDeparture: FtcAnswer | null;
    khcReference: string | null;
    comments: string | null;
}

export const updateEntry = createAsyncThunk<
    FtcSessionEntry,
    UpdateEntryPayload,
    { rejectValue: string }
>(
    'ftc/updateEntry',
    async ({ entryId, ...data }, { rejectWithValue }) => {
        try {
            const response: FtcSessionEntryResponse = await fetchApiAuth({
                method: 'PUT',
                url: `/ftc/entries/${entryId}`,
                body: data,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to update FTC entry');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export interface UpdateEntryStatusPayload {
    entryId: number;
    status: 'ftc' | 'guarding' | 'no_access';
}

export const updateEntryStatus = createAsyncThunk<
    FtcSessionEntry,
    UpdateEntryStatusPayload,
    { rejectValue: string }
>(
    'ftc/updateEntryStatus',
    async ({ entryId, status }, { rejectWithValue }) => {
        try {
            const response: FtcSessionEntryResponse = await fetchApiAuth({
                method: 'PATCH',
                url: `/ftc/entries/${entryId}/status`,
                body: { status },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to update store status');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const removeEntry = createAsyncThunk<
    number,
    number,
    { rejectValue: string }
>(
    'ftc/removeEntry',
    async (entryId, { rejectWithValue }) => {
        try {
            await fetchApiAuth({
                method: 'DELETE',
                url: `/ftc/entries/${entryId}`,
            });
            return entryId;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to remove FTC entry');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const sendReport = createAsyncThunk<
    FtcMessagePayload,
    void,
    { rejectValue: string }
>(
    'ftc/sendReport',
    async (_, { rejectWithValue }) => {
        try {
            const response: FtcMessageResponse = await fetchApiAuth({
                method: 'POST',
                url: '/ftc/send-report',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to send FTC report');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const fetchProjectGuardings = createAsyncThunk<
    ProjectGuarding[],
    void,
    { rejectValue: string }
>(
    'ftc/fetchProjectGuardings',
    async (_, { rejectWithValue }) => {
        try {
            const response: ProjectGuardingListResponse = await fetchApiAuth({
                method: 'GET',
                url: '/ftc/project-guarding',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to load project guarding');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const createProjectGuarding = createAsyncThunk<
    ProjectGuarding,
    CreateProjectGuardingPayload,
    { rejectValue: string }
>(
    'ftc/createProjectGuarding',
    async (payload, { rejectWithValue }) => {
        try {
            const response: ProjectGuardingResponse = await fetchApiAuth({
                method: 'POST',
                url: '/ftc/project-guarding',
                body: payload,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to add project guarding');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const deleteProjectGuarding = createAsyncThunk<
    number,
    number,
    { rejectValue: string }
>(
    'ftc/deleteProjectGuarding',
    async (entryId, { rejectWithValue }) => {
        try {
            await fetchApiAuth({
                method: 'DELETE',
                url: `/ftc/project-guarding/${entryId}`,
            });
            return entryId;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to remove project guarding');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);

export const sendUpdate = createAsyncThunk<
    FtcMessagePayload,
    void,
    { rejectValue: string }
>(
    'ftc/sendUpdate',
    async (_, { rejectWithValue }) => {
        try {
            const response: FtcMessageResponse = await fetchApiAuth({
                method: 'POST',
                url: '/ftc/send-update',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const message = fallbackMessage(errorMessage, 'Failed to send FTC update');
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: message });
            }
            return rejectWithValue(message);
        }
    },
);
